import type { LanguageCode } from "../languages/language-codes";
import type { LanguageAttributes } from "./extension-language";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor } from "@tiptap/react";
import { LanguageCommand } from "../languages/LanguageCommand";
import { Icon } from "@mdi/react";
import { mdiTranslate } from "@mdi/js";
import { cn } from "@acdc2/ui/lib/utils";
import { Button } from "@acdc2/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@acdc2/ui/components/tooltip";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@acdc2/ui/components/popover";

type Props = {
  editor: Editor;
};

export function LanguagePicker({ editor }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const attributes: LanguageAttributes = editor.getAttributes("language");
  const currentCode = attributes.code;

  const onSelect = (code: LanguageCode) => {
    if (currentCode === code) {
      editor.chain().focus().unsetLanguage().run();
    } else {
      editor.chain().focus().setLanguage(code).run();
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                className={cn("size-8", { "bg-muted": currentCode })}
                variant="ghost"
                size="sm"
                role="combobox"
                aria-expanded={open}
              >
                {currentCode ? (
                  <div className="py-1 font-bold">
                    {currentCode.toUpperCase()}
                  </div>
                ) : (
                  <Icon path={mdiTranslate} size="1.5em" />
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{t("LanguagePicker.tooltip")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="p-0 mx-2">
        <LanguageCommand value={currentCode} onSelect={onSelect} />
      </PopoverContent>
    </Popover>
  );
}
